import { FC, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PunchupTooltip.module.css';

let tooltipCounter = 0;

export interface PunchupTooltipProps {
	/**
	 * Primary body text — always visible when the tooltip is open.
	 * Use for the metric name, formula, or explanation.
	 */
	mainText: string;
	/**
	 * Optional monospaced code / formula block shown between main and secondary text.
	 * Rendered in a greyed surface box — use for formulas, field names, or computed examples.
	 */
	codeText?: string;
	/**
	 * Optional supplementary text shown below code (or below main when no code).
	 * Use for caveats, units, or data-source notes.
	 */
	secondaryText?: string;
	/** The element that triggers open/close on hover and focus. */
	children: ReactNode;
	/**
	 * Maximum panel width in px. Default: 280.
	 * The panel is also constrained to never overflow the viewport.
	 */
	maxWidth?: number;
	/**
	 * When set, the trigger becomes a keyboard-focusable, screen-reader-named control: it receives
	 * `tabIndex={0}`, `role="button"`, and `aria-label={triggerLabel}` (so the tooltip opens on Tab focus and
	 * the control announces a name). Use for icon-only triggers (e.g. a help icon) that carry no text of their
	 * own. Omit when the child is already a focusable, labelled element (e.g. a `<button>`) — focus bubbles to
	 * the wrapper, so adding `tabIndex` here would create a second, redundant tab stop.
	 */
	triggerLabel?: string;
	/**
	 * Compact label mode: drops the prose panel's 180px min-width and tightens padding so the panel
	 * shrink-wraps a single short word on one line. Use for icon-button hover captions (e.g. an icon-only
	 * action row); omit for the default multi-segment metric/field explanation panel.
	 */
	compact?: boolean;
}

interface TooltipPos {
	top: number;
	left: number;
}

const OFFSET = 6; // px gap between trigger edge and panel edge
const MARGIN = 8; // min px from viewport boundary

function computePos(trigger: DOMRect, panelH: number, panelW: number): TooltipPos {
	const vw = window.innerWidth;
	const vh = window.innerHeight;

	// Prefer below; fall back to above when there is not enough space below.
	const spaceBelow = vh - trigger.bottom - OFFSET;
	const spaceAbove = trigger.top - OFFSET;
	const placeBelow = spaceBelow >= panelH || spaceBelow >= spaceAbove;

	const rawTop = placeBelow ? trigger.bottom + OFFSET : trigger.top - panelH - OFFSET;

	// Center panel on trigger, then clamp to viewport.
	const rawLeft = trigger.left + trigger.width / 2 - panelW / 2;

	return {
		top: Math.max(MARGIN, Math.min(rawTop, vh - panelH - MARGIN)),
		left: Math.max(MARGIN, Math.min(rawLeft, vw - panelW - MARGIN)),
	};
}

export const PunchupTooltip: FC<PunchupTooltipProps> = ({
	mainText,
	codeText,
	secondaryText,
	children,
	maxWidth = 280,
	triggerLabel,
	compact = false,
}) => {
	const id = useMemo(() => `punchup-tooltip-${++tooltipCounter}`, []);
	const [isOpen, setIsOpen] = useState(false);
	const [pos, setPos] = useState<TooltipPos | null>(null);
	const triggerRef = useRef<HTMLSpanElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => {
		setIsOpen(false);
		setPos(null);
	}, []);

	// Measure the hidden panel and compute position before the browser paints.
	// Runs synchronously after DOM update so there is no visible flash.
	useLayoutEffect(() => {
		if (!isOpen || !triggerRef.current || !panelRef.current) return;
		const triggerRect = triggerRef.current.getBoundingClientRect();
		const { offsetHeight, offsetWidth } = panelRef.current;
		setPos(computePos(triggerRect, offsetHeight, offsetWidth));
	}, [isOpen]);

	return (
		<>
			<span
				ref={triggerRef}
				onMouseEnter={handleOpen}
				onMouseLeave={handleClose}
				onFocus={handleOpen}
				onBlur={handleClose}
				className={styles.trigger}
				tabIndex={triggerLabel ? 0 : undefined}
				role={triggerLabel ? 'button' : undefined}
				aria-label={triggerLabel}
				aria-describedby={isOpen ? id : undefined}
			>
				{children}
			</span>

			{isOpen &&
				createPortal(
					<div
						id={id}
						ref={panelRef}
						role="tooltip"
						className={`${styles.panel}${compact ? ` ${styles.panelCompact}` : ''}`}
						style={{
							maxWidth,
							top: pos ? pos.top : -9999,
							left: pos ? pos.left : -9999,
							visibility: pos ? 'visible' : 'hidden',
						}}
					>
						<p className={styles.mainText}>{mainText}</p>
						{codeText && <pre className={styles.codeBlock}>{codeText}</pre>}
						{secondaryText && <p className={styles.secondaryText}>{secondaryText}</p>}
					</div>,
					document.body
				)}
		</>
	);
};
